import { Headshot, HeadshotKeys, LookupKeys, Social } from '@ag-common-lib/public-api';
import { TemplateBuilderBlock } from './template-builder-block.model';
import { TemplateBuilderModule } from './template-builder-module.model';
import { BaseModel } from '@ag-common-lib/lib/models/base.model';
import { NotificationOptInGroupType } from '@ag-common-lib/lib';
import { TemplateBuilderWidgetTypes } from './template-builder-widget.model';

export class TemplateCommonSettings {
  /** @deprecated Use contentBackgroundColor of the block instead */
  contentBackgroundColor: string;

  backgroundColor: string;
  textColor: string;
  contentMaxWidth: number;
  contentAlignment: 'left' | 'center';
  fontFamily: string;
  fontWeight: 'regular' | 'bold';

  constructor(settings?: Partial<TemplateCommonSettings>) {
    this.contentBackgroundColor = settings?.contentBackgroundColor ?? '#fff';
    this.backgroundColor = settings?.backgroundColor ?? '#f2f2f2';
    this.textColor = settings?.textColor ?? '#333';
    this.contentMaxWidth = settings?.contentMaxWidth ?? 1360;
    this.contentAlignment = settings?.contentAlignment ?? 'center';
    this.fontFamily = settings?.fontFamily ?? TemplateFontFamilies.helvetica;
    this.fontWeight = settings?.fontWeight ?? 'regular';
  }
}

export const PB_PREVIEW_PREFIX = 'page-builder-preview';

export enum TemplateFontFamilies {
  arial = 'arial',
  courierNew = 'courier new',
  georgia = 'georgia',
  impact = 'impact',
  lucidaConsole = 'lucida console',
  tahoma = 'tahoma',
  timesNewRoman = 'times new roman',
  verdana = 'verdana',
  helvetica = 'helvetica',
}

export const TemplateFontFamiliesLookup = [
  { value: TemplateFontFamilies.arial, description: 'Arial' },
  { value: TemplateFontFamilies.courierNew, description: 'Courier New' },
  { value: TemplateFontFamilies.georgia, description: 'Georgia' },
  { value: TemplateFontFamilies.impact, description: 'Impact' },
  { value: TemplateFontFamilies.lucidaConsole, description: 'Lucida Console' },
  { value: TemplateFontFamilies.tahoma, description: 'Tahoma' },
  { value: TemplateFontFamilies.timesNewRoman, description: 'Times New Roman' },
  { value: TemplateFontFamilies.verdana, description: 'Verdana' },
  { value: TemplateFontFamilies.helvetica, description: 'Helvetica' },
];

export const TemplateLineHeightLookup = [
  { value: 'inherit', description: 'Def.' },
  { value: '0.5', description: '0.5' },
  { value: '0.8', description: '0.8' },
  { value: '1', description: '1.0' },
  { value: '1.2', description: '1.2' },
  { value: '1.4', description: '1.4' },
  { value: '1.6', description: '1.6' },
  { value: '1.8', description: '1.8' },
  { value: '2', description: '2.0' },
  { value: '3', description: '3.0' },
];

export enum TemplateKeys {
  type = 'type',
  subtype = 'subtype',
  previewDataUrl = 'previewDataUrl',
  headshot = 'headshot',
  name = 'name',
  description = 'description',
  rows = 'rows',
  optInGroup = 'optInGroup',
  commonSettings = 'commonSettings',
  externalLinks = 'externalLinks',
  requiredFields = 'requiredFields',
  /* Default Social Media Settings */
  socialMediaSettings = 'socialMediaSettings',
}

export const TEMPLATE_BUILDER_LOOKUP = [
  {
    value: TemplateKeys.name,
    description: 'Name',
  },
  {
    value: TemplateKeys.description,
    description: 'Description',
  },
];

export enum TemplateTypes {
  page = 'page',
  email = 'email',
}

export const TemplateTypesMap = new Map([
  [TemplateTypes.page, 'Page'],
  [TemplateTypes.email, 'Email'],
]);

export const TEMPLATE_TYPES_LOOKUP = [TemplateTypes.page, TemplateTypes.email].map(value => {
  return {
    [LookupKeys.value]: value,
    [LookupKeys.description]: TemplateTypesMap.get(value),
  };
});

export enum TemplateSubTypes {
  page = 'page',
  block = 'block',
  module = 'module',
}

export enum TemplateRequiredFieldTypes {
  activitiesDay = 'activitiesDay',
  excursionsSet = 'excursionsSet',
}

export enum TemplateExcursionsSet {
  main = 'main',
  chairmanExtension = 'chairmanExtension',
  all = 'all',
}

export const DEFAULT_TEMPLATE_EXCURSIONS_SET = TemplateExcursionsSet.main;

export interface TemplateSocialMediaSettings {
  socials: Social[];
}

export type TemplateRequiredField = {
  widgetId: string;
  type: TemplateRequiredFieldTypes;
  label?: string;
  widgetType?: TemplateBuilderWidgetTypes;
};

export const TemplateSubTypesMap = new Map([
  [TemplateSubTypes.page, 'Page'],
  [TemplateSubTypes.block, 'Block Template'],
  [TemplateSubTypes.module, 'Global Block'],
]);

export const PAGE_TEMPLATE_SUBTYPES_LOOKUP = [
  TemplateSubTypes.page,
  TemplateSubTypes.block,
  TemplateSubTypes.module,
].map(value => {
  return {
    [LookupKeys.value]: value,
    [LookupKeys.description]: TemplateSubTypesMap.get(value),
  };
});

export class Template extends BaseModel {
  [TemplateKeys.type]: TemplateTypes;
  [TemplateKeys.subtype]: TemplateSubTypes;
  [TemplateKeys.name]: string;
  [TemplateKeys.description]: string;
  [TemplateKeys.previewDataUrl]?: string;
  [TemplateKeys.headshot]?: Headshot = { [HeadshotKeys.wasabiPath]: '' };
  [TemplateKeys.optInGroup]?: NotificationOptInGroupType;
  [TemplateKeys.rows]: Array<TemplateBuilderBlock | TemplateBuilderModule> = [];
  [TemplateKeys.commonSettings] = Object.assign({}, new TemplateCommonSettings());
  [TemplateKeys.externalLinks]?: string[] = [];
  [TemplateKeys.requiredFields]?: TemplateRequiredField[] = [];
  [TemplateKeys.socialMediaSettings]?: TemplateSocialMediaSettings;
}
